import { Link, Outlet, useLocation } from "react-router-dom";
import {
  Sidebar, SidebarContent, SidebarHeader, SidebarMenu, SidebarMenuItem,
  SidebarMenuButton, SidebarProvider, SidebarTrigger, SidebarRail, SidebarFooter,
} from "@/components/ui/sidebar";
import { LayoutDashboard, Users, FileText } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useGinjaBrand } from "@/hooks/use-ginja-brand";
import { useAuth } from "@/contexts/auth-context";
import { cn } from "@/lib/utils";
import { AO_PERM } from "@/services/ginjaAuth";

const mainNav = [
  { title: "Dashboard", icon: LayoutDashboard, href: "/" },
  { title: "Policies", icon: FileText, href: "/policies" },
  {
    title: "Members",
    icon: Users,
    href: "/members",
    /** Ginja: show when any member-area permission exists; legacy CAS keeps link. */
    permissionKeys: [
      AO_PERM.viewMember,
      AO_PERM.addMember,
      AO_PERM.bulkUpload,
      AO_PERM.editMember,
    ] as const,
  },
  // { title: "Settings", icon: Settings, href: "/settings" },
] as const;

export function ProtectedLayout() {
  const location = useLocation();
  const brand = useGinjaBrand();
  const { user: authUser, hasAoPermission } = useAuth();

  const user = {
    name: authUser?.name ?? "User",
    email: authUser?.email ?? "",
    avatar: authUser?.avatar ?? "",
  };

  const brandLogo = brand?.logo_url?.trim();

  return (
    <SidebarProvider>
      <div className="flex h-screen w-full overflow-hidden">
        <Sidebar collapsible="icon" className="border-r border-sidebar-border">
          <SidebarHeader className="px-5 py-5 border-b border-sidebar-border group-data-[collapsible=icon]:px-2 group-data-[collapsible=icon]:py-4">
            <div className="flex items-center gap-2.5 min-w-0 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:gap-0">
              {brandLogo ? (
                <img
                  src={brandLogo}
                  alt=""
                  referrerPolicy="no-referrer"
                  decoding="async"
                  className="h-8 w-8 rounded-lg object-contain shrink-0 group-data-[collapsible=icon]:h-8 group-data-[collapsible=icon]:w-8"
                />
              ) : (
                <div className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center shrink-0 group-data-[collapsible=icon]:h-8 group-data-[collapsible=icon]:w-8">
                  <span className="text-sm font-bold text-primary-foreground">A</span>
                </div>
              )}
              <div className="min-w-0 leading-tight group-data-[collapsible=icon]:hidden">
                <span className="block text-[15px] font-semibold text-primary tracking-tight">Assisted</span>
                <span className="block text-[15px] font-semibold text-primary tracking-tight">Onboarding</span>
              </div>
            </div>
          </SidebarHeader>

          <SidebarContent className="px-3 py-4 group-data-[collapsible=icon]:px-2">
            <SidebarMenu className="space-y-1">
              {mainNav.map((item) => {
                if (
                  "permissionKeys" in item &&
                  item.permissionKeys &&
                  !item.permissionKeys.some((k) => hasAoPermission(k))
                ) {
                  return null;
                }
                const isActive =
                  item.href === "/"
                    ? location.pathname === "/"
                    : location.pathname.startsWith(item.href);
                return (
                  <SidebarMenuItem key={item.href}>
                    <SidebarMenuButton
                      asChild
                      tooltip={item.title}
                      className={cn(
                        "h-10 rounded-lg px-3 transition-colors",
                        "group-data-[collapsible=icon]:!size-8 group-data-[collapsible=icon]:!shrink-0 group-data-[collapsible=icon]:!p-2",
                        isActive
                          ? "bg-sidebar-accent text-sidebar-accent-foreground font-medium"
                          : "text-sidebar-foreground hover:bg-sidebar-accent/60",
                      )}
                    >
                      <Link to={item.href}>
                        <item.icon className="h-[18px] w-[18px]" />
                        <span className="group-data-[collapsible=icon]:hidden">{item.title}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarContent>

          <SidebarFooter className="p-3 border-t border-sidebar-border group-data-[collapsible=icon]:p-2 group-data-[collapsible=icon]:flex group-data-[collapsible=icon]:items-center group-data-[collapsible=icon]:justify-center">
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton
                  size="lg"
                  type="button"
                  tabIndex={-1}
                  tooltip={
                    user.email ? `${user.name} — ${user.email}` : user.name
                  }
                  className={cn(
                    "h-12 cursor-default rounded-lg px-3 hover:bg-sidebar-accent/50 focus-visible:ring-0",
                    "group-data-[collapsible=icon]:!size-10 group-data-[collapsible=icon]:!shrink-0 group-data-[collapsible=icon]:!p-0",
                  )}
                  aria-label={user.email ? `${user.name}, ${user.email}` : user.name}
                >
                  <Avatar className="h-8 w-8 shrink-0 rounded-lg">
                    <AvatarImage src={user.avatar} alt="" />
                    <AvatarFallback className="rounded-lg bg-primary/10 text-primary text-xs font-medium">
                      {user.name.split(" ").map((w: string) => w[0]).join("").slice(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="grid min-w-0 flex-1 text-left text-sm leading-tight group-data-[collapsible=icon]:hidden">
                    <span className="truncate font-medium text-[13px]">{user.name}</span>
                    {user.email ? (
                      <span className="truncate text-[11px] text-muted-foreground">{user.email}</span>
                    ) : null}
                  </div>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarFooter>
          <SidebarRail />
        </Sidebar>

        <main className="flex-1 overflow-y-auto bg-background scrollbar-thin">
          <div className="sticky top-0 z-10 bg-background/80 backdrop-blur-sm border-b px-4 py-2.5 lg:px-6">
            <SidebarTrigger />
          </div>
          <div className="px-4 py-5 lg:px-8 lg:py-6 max-w-[1400px] mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
}
